import { promiseTimeout } from '@vueuse/core';
import types from '../mutation-types';
import authAPI from '../../api/auth';

import { setUser, clearCookiesOnLogout } from '../utils/api';

// Retry validate_token on transient (non-401) failures before giving up, so a
// momentary backend/network blip doesn't leave the user in an empty state.
const VALIDITY_CHECK_MAX_RETRIES = 3;
const VALIDITY_CHECK_RETRY_DELAY = 1000;

const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
  },
  uiFlags: {
    isFetching: true,
    hasConnectionError: false,
  },
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  // The session is a super admin "Impersonate user" login (set by the server).
  isImpersonating($state) {
    return $state.currentUser.impersonated === true;
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentUserAutoOffline($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.auto_offline;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.role;
  },

  getCurrentCustomRoleId($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.custom_role_id;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  getMessageSignature($state) {
    const { message_signature: messageSignature } = $state.currentUser;

    return messageSignature || '';
  },

  getCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount || {};
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    return accounts;
  },
};

// actions
export const actions = {
  async validityCheck(context) {
    let lastError;
    for (let attempt = 0; attempt <= VALIDITY_CHECK_MAX_RETRIES; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await authAPI.validityCheck();
        const currentUser = response.data.payload.data;
        setUser(currentUser);
        context.commit(types.SET_CURRENT_USER, currentUser);
        return;
      } catch (error) {
        // Only a 401 means the session is genuinely invalid -> log out.
        if (error?.response?.status === 401) {
          clearCookiesOnLogout();
          return;
        }
        // Any other failure (5xx, timeout, network blip) is transient. Retry
        // instead of silently leaving currentUser at its initial empty state,
        // which would otherwise look like a logged-in user with no accounts.
        lastError = error;
        if (attempt < VALIDITY_CHECK_MAX_RETRIES) {
          // eslint-disable-next-line no-await-in-loop
          await promiseTimeout(VALIDITY_CHECK_RETRY_DELAY * (attempt + 1));
        }
      }
    }
    // Retries exhausted: surface the failure rather than swallowing it so the
    // app doesn't proceed as if the user has no accounts.
    throw lastError;
  },
  async setUser({ commit, dispatch }) {
    if (!authAPI.hasAuthCookie()) {
      commit(types.CLEAR_USER);
      commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
      return;
    }
    try {
      await dispatch('validityCheck');
      commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
    } catch (error) {
      // validate_token kept failing after retries (backend outage). Surface a
      // "connection lost" screen instead of bouncing a still-authenticated
      // user to /login. Re-throw so the router guard skips navigation.
      commit(types.SET_CURRENT_USER_UI_FLAGS, {
        isFetching: false,
        hasConnectionError: true,
      });
      throw error;
    }
  },
  logout({ commit }) {
    commit(types.CLEAR_USER);
  },

  updateProfile: async ({ commit }, params) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profileUpdate(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      throw error;
    }
  },

  updatePassword: async ({ commit }, params) => {
    const response = await authAPI.profilePasswordUpdate(params);
    commit(types.SET_CURRENT_USER, response.data);
  },

  deleteAvatar: async ({ commit }) => {
    try {
      const response = await authAPI.deleteAvatar();
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  deleteEmailSignaturePicture: async ({ commit }) => {
    const response = await authAPI.deleteEmailSignaturePicture();
    commit(types.SET_CURRENT_USER, response.data);
  },

  updateUISettings: async ({ commit }, params) => {
    try {
      commit(types.SET_CURRENT_USER_UI_SETTINGS, params);
      const response = await authAPI.updateUISettings(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  updateAvailability: async (
    { commit, dispatch, getters: _getters },
    params
  ) => {
    const previousStatus = _getters.getCurrentUserAvailability;

    try {
      // optimisticly update current status
      commit(types.SET_CURRENT_USER_AVAILABILITY, params.availability);
      const response = await authAPI.updateAvailability(params);
      const userData = response.data;
      const { id } = userData;
      commit(types.SET_CURRENT_USER, response.data);
      dispatch('agents/updateSingleAgentPresence', {
        id,
        availabilityStatus: params.availability,
      });
    } catch (error) {
      // revert back to previous status if update fails
      commit(types.SET_CURRENT_USER_AVAILABILITY, previousStatus);
    }
  },

  updateAutoOffline: async (
    { commit, getters: _getters },
    { accountId, autoOffline }
  ) => {
    const previousAutoOffline = _getters.getCurrentUserAutoOffline;

    try {
      commit(types.SET_CURRENT_USER_AUTO_OFFLINE, autoOffline);
      const response = await authAPI.updateAutoOffline(accountId, autoOffline);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      commit(types.SET_CURRENT_USER_AUTO_OFFLINE, previousAutoOffline);
    }
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, data[$state.currentUser.id]);
    }
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },

  resendConfirmation: async () => {
    try {
      await authAPI.resendConfirmation();
    } catch (error) {
      // Ignore error
    }
  },

  regenerateAccessToken: async ({ commit }) => {
    const response = await authAPI.regenerateAccessToken();
    commit(types.SET_CURRENT_USER, response.data);
  },
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },
  [types.SET_CURRENT_USER_AUTO_OFFLINE](_state, autoOffline) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, autoOffline: autoOffline };
      }
      return account;
    });

    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = initialState.currentUser;
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    _state.currentUser = currentUser;
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    _state.currentUser = {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    };
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
